import { DateAxis, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, Label } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { SOV_MIX_COLORS, WHITE, BLACK } from "../../../constants";
import { disposeChart } from "../helpers/chartHelpers";
import { configureDateAxis, configureValueAxis, lineSeriesConfiguration } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import {
  dateAxisLabelFormatter_MonthlyFY,
  dateAxisLabelFormatter_QuarterlyFY,
  dateAxisLabelFormatter_YearlyFY,
  dateBrazilAndThailandAxisLabelFormatter_QuarterlyFY,
  dateChinaAxisLabelFormatter_QuarterlyFY,
  dateChinaAxisLabelFormatter_YearlyFY,
  formatBrazilAndThailandDateAxisLabelToQuarterlyFY,
  formatChinaDateAxisLabelToQuarterlyFY,
  formatChinaDateAxisLabelToYearlyFY,
  formatDateAxisLabelToQuarterlyFY,
  formatDateAxisLabelToYearlyFY,
} from "../helpers/pmoConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  granularity: string;
  theme: ThemeInt;
  chartId: string;
  seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
  market: string;
  diff: number;
  legendPosition?: "right" | "left" | "top" | "bottom";
  showSOVchartText?: boolean;
  showPTVAchartText?: boolean;
  numberFormat?: string;
  axisRangeValue?: number | null;
  noValueAxisMin?: boolean;
}

class BrandPerformanceTrendChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chartId = this.props.chartId;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId);
      this.initChart();
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  createSOVSOMTextLabel = (chart: XYChart, theme: ThemeInt, text: string) => {
    const label = chart.plotContainer.createChild(Label);
    label.fontSize = 12;
    label.text = text;
    label.fill = theme == "dark" ? color(WHITE) : color(BLACK);
    label.align = "left";
    label.isMeasured = false;
    label.x = 0;
    label.y = -20;
  };

  initChart() {
    const {
      data,
      granularity,
      theme,
      seriesList,
      percentageChart,
      nissanCompetitor,
      market,
      diff,
      legendPosition = "top",
      showSOVchartText = false,
      showPTVAchartText = false,
      numberFormat = percentageChart || showPTVAchartText ? "#,###.0" : "#,###",
      axisRangeValue,
      noValueAxisMin,
    } = this.props;

    // Global chart configuration
    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.numberFormatter.numberFormat = "#a";
    this.chart.dateFormatter.inputDateFormat = "yyyy-MM-dd";
    this.chart.data = data;
    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.colors.list = SOV_MIX_COLORS.map((chartColor) => color(chartColor));
    this.chart.legend.marginBottom = 30;

    this.chart.legend.maxHeight = 100;
    this.chart.legend.scrollable = true;
    this.chart.legend.position = legendPosition;
    this.chart.legend.reverseOrder = false;

    showSOVchartText && this.createSOVSOMTextLabel(this.chart, theme, "*1.0 = 'SOV is at the same level as SOM'");
    showPTVAchartText && this.createSOVSOMTextLabel(this.chart, theme, "Nissan = 100");

    // Create data chart axes
    const dateAxis = this.chart.xAxes.push(new DateAxis());
    configureDateAxis(dateAxis, theme);
    dateAxis.renderer.cellStartLocation = 0.0;
    dateAxis.renderer.cellEndLocation = 0.0;
    dateAxis.renderer.minGridDistance = 45;

    // add logic to display this for more than one year monthly
    if ((diff > 360 && granularity == "monthly") || diff > 720) {
      dateAxis.renderer.labels.template.rotation = -90;
      dateAxis.renderer.labels.template.verticalCenter = "middle";
      dateAxis.renderer.labels.template.horizontalCenter = "left";
    }
    granularity === "monthly" && dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_MonthlyFY);
    granularity === "quarterly" &&
      market !== "China" &&
      market !== "Brazil" &&
      market !== "Thailand" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_QuarterlyFY);
    granularity === "yearly" &&
      market !== "China" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateAxisLabelFormatter_YearlyFY);

    granularity === "quarterly" &&
      market == "China" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateChinaAxisLabelFormatter_QuarterlyFY);
    granularity === "yearly" &&
      market == "China" &&
      dateAxis.renderer.labels.template.adapter.add("text", dateChinaAxisLabelFormatter_YearlyFY);

    granularity === "quarterly" &&
      (market === "Brazil" || market === "Thailand") &&
      dateAxis.renderer.labels.template.adapter.add("text", dateBrazilAndThailandAxisLabelFormatter_QuarterlyFY);

    // Creates value axis
    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.extraMax = 0.1;

    if (!noValueAxisMin) {
      valueAxis.min = 0;
    }

    if (axisRangeValue) {
      const range = valueAxis.axisRanges.create();
      range.value = axisRangeValue;
      range.grid.strokeWidth = 3;
      range.grid.strokeOpacity = 1;
    }

    if (this.props.percentageChart) {
      // valueAxis.max = 100;
      valueAxis.extraMax = 0;
    }

    if (showSOVchartText || showPTVAchartText) {
      const maxValue = Math.max(...this.chart.data.map((item) => Math.max(...seriesList.map((series) => item[series.field]))));
      valueAxis.paddingLeft = 20;
      valueAxis.layout = "absolute";

      // Add top title
      const topTitle = valueAxis.axisRanges.create();
      topTitle.value = valueAxis.min ?? 0;
      if (showSOVchartText) {
        topTitle.label.text = "More \n Aggressive";
      } else {
        // break text into two lines
        topTitle.label.text = "Higher \n than Nissan";
      }
      topTitle.label.inside = false;
      topTitle.label.valign = "top";
      topTitle.label.dy = -50;
      topTitle.label.fontSize = 10;
      topTitle.grid.disabled = true;

      // Add bottom title
      const bottomTitle = valueAxis.axisRanges.create();
      bottomTitle.value = valueAxis.max ?? maxValue;
      if (showSOVchartText) {
        bottomTitle.label.text = "Less \n Aggressive";
      } else {
        bottomTitle.label.text = "Lower \n than Nissan";
      }
      bottomTitle.label.inside = false;
      bottomTitle.label.valign = "bottom";
      bottomTitle.label.dy = 40;
      bottomTitle.label.fontSize = 10;
      bottomTitle.grid.disabled = true;

      // Add center arrow
      const centerArrow = valueAxis.axisRanges.create();
      centerArrow.value = (valueAxis.min ?? 0 + (valueAxis.max ?? maxValue)) / 2;
      const colorSVG = theme == "dark" ? color(WHITE) : color(BLACK);
      centerArrow.label.html = `
        <svg width="20" height="100%" viewBox="0 0 20 100" preserveAspectRatio="none" style="height: 100%;">
          <line x1="10" y1="0" x2="10" y2="100" stroke="${colorSVG}" stroke-width="2"/>
          <polygon points="5,10 10,0 15,10" fill="${colorSVG}"/>
          <polygon points="5,90 10,100 15,90" fill="${colorSVG}"/>

        </svg>
      `;
      centerArrow.label.dx = -40;
      centerArrow.label.inside = false;
      centerArrow.label.valign = "middle";
      centerArrow.label.fontSize = 10;
      centerArrow.grid.disabled = true;
    }

    valueAxis.numberFormatter.numberFormat = "#.#a";
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return text + (percentageChart ? "%" : "");
    });

    seriesList?.forEach(({ name, field, hide, colour }, idx) => {
      const series = this.chart.series.push(new LineSeries());
      const bullet = lineSeriesConfiguration(series, {
        valueY: `${field}`,
        name: `${name}`,
      });
      series.hiddenInLegend = hide ?? false;
      series.xAxis = dateAxis;
      series.dataFields.dateX = "date";
      if (colour) {
        series.fill = color(colour);
        series.stroke = color(colour);
      }
      bullet.adapter.add("tooltipText", (text, target) => {
        const date = target?.dataItem?.dates?.dateX;
        if (granularity === "quarterly") {
          //@ts-ignore
          const dateVal =
            market == "China"
              ? //@ts-ignore

              formatChinaDateAxisLabelToQuarterlyFY(date)
              : market == "Brazil" || market == "Thailand"
                ? //@ts-ignore

                formatBrazilAndThailandDateAxisLabelToQuarterlyFY(date)
                : //@ts-ignore

                formatDateAxisLabelToQuarterlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] ${dateVal} ${name}[/ font-size: var(--regular_font_size);]: {valueY.value.formatNumber("${numberFormat}")}${percentageChart ? "%" : ""
            }`;
        } else if (granularity === "yearly") {
          //@ts-ignore
          const dateVal = market == "China" ? formatChinaDateAxisLabelToYearlyFY(date) : formatDateAxisLabelToYearlyFY(date);
          text = `[bold; font-size: var(--regular_font_size);] ${dateVal} ${name}[/ font-size: var(--regular_font_size);]: {valueY.value.formatNumber("${numberFormat}")}${percentageChart ? "%" : ""
            }`;
        } else {
          text = `[bold; font-size: var(--regular_font_size);]{dateX.formatDate('MMM YYYY')} ${name} [/ font-size: var(--regular_font_size);]: {valueY.value.formatNumber("${numberFormat}")}${percentageChart ? "%" : ""
            }`;
        }

        return text;
      });

      // --- Updated legend filtering logic to match any number of leading underscores ---
      if (hide != undefined && !hide) {
        const baseField = field.replace(/^_+/, "");
        series.events.on("hidden", () => {
          this.chart.series.each((s) => {
            const sField = s.dataFields.valueY || "";
            if (sField.replace(/^_+/, "") === baseField && s !== series) {
              s.hide();
            }
          });
        });
        series.events.on("shown", () => {
          this.chart.series.each((s) => {
            const sField = s.dataFields.valueY || "";
            if (sField.replace(/^_+/, "") === baseField && s !== series) {
              s.show();
            }
          });
        });
      }
    });

    // --- Data normalization to prevent extra lines between points ---
    // Ensure all dates are present for all series, and missing values are set to null
    if (Array.isArray(data) && Array.isArray(seriesList)) {
      // 1. Collect all unique dates
      const allDates = Array.from(new Set(data.map((d) => d.date))).sort();
      // 2. For each date, ensure all series fields are present and values are numbers/null
      this.chart.data = allDates.map((date) => {
        const row = data.find((d) => d.date === date) || { date };
        const filled: Record<string, any> = { date };
        seriesList.forEach(({ field }) => {
          const value = row[field];
          // Convert to number if possible, else null
          if (value === undefined || value === null || value === "") {
            filled[field] = null;
          } else {
            const num = Number(value);
            filled[field] = isNaN(num) ? null : num;
          }
        });
        return filled;
      });
    } else {
      this.chart.data = data;
    }
  }
  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
      </div>
    );
  }
}

export default BrandPerformanceTrendChart;
